import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { isFilterableAttribute } from './attributeFilters';

type Props = {
  /** Все атрибуты из системной конфигурации */
  availableAttributes: ConversationAttributeItem[];
  /** Пути уже добавленных в панель атрибутов — исключаются из выбора */
  activePaths: ReadonlySet<string>;
  /** Колбэк добавления атрибута по его `path` */
  onAdd: (path: string) => void;
};

/**
 * Кнопка-поповер для добавления атрибута в активную панель фильтров дашборда качества.
 * Атрибуты с `dataType = STRUCTURE` и уже добавленные в панель исключены из списка.
 */
export const AttributeFilterPicker: FC<Props> = ({ availableAttributes, activePaths, onAdd }) => {
  const { t } = useTranslation('qualityDashboard');
  const [isOpen, setIsOpen] = useState(false);
  // Управляемый поиск по подстроке: фильтруем варианты сами, чтобы
  // регистронезависимый поиск работал по `pathDescription`/`name`/`path`.
  const [search, setSearch] = useState('');

  const options = useMemo(
    () =>
      availableAttributes
        .filter(isFilterableAttribute)
        .filter((a) => !activePaths.has(a.path))
        .map((a) => ({
          value: a.path,
          label: a.pathDescription || a.name || a.path,
        })),
    [availableAttributes, activePaths],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, search]);

  const handleSelect = (path: string) => {
    onAdd(path);
    setIsOpen(false);
    setSearch('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Сбрасываем поисковую строку при закрытии, чтобы повторное открытие
    // показывало полный список доступных атрибутов.
    if (!open) setSearch('');
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm" icon={<PlusIcon />}>
          {t('filters.addAttribute')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('filters.selectAttribute')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('filters.noAttributesFound')}</CommandEmpty>
            {filteredOptions.map((opt) => (
              <CommandItem key={opt.value} value={opt.value} onSelect={handleSelect}>
                {opt.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
